import { ReactElement, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import { icon_minus, icon_plus } from '@gn-audio/icons';
import {
  EcommerceItem,
  GtmEventType,
  ProductService,
  ProductWithAttributes,
  ServiceType,
  SFCCProductItem,
} from '@gn-audio/jss-core';

import { SiteFeature } from '../../../types';
import { isNotNil, TrackingContext, useFallbackTranslation, useSiteSettings } from '../../../utils';
import { Button } from '../../Button/Button';
import { FeatureToggle } from '../../FeatureToggle';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { useBasket } from '../BasketContext';
import { GiftWrapping } from '../GiftWrapping/GiftWrapping';
import { mapToBasketService } from '../GiftWrapping/giftWrappingUtils';
import { createEcommercePayload } from '../utilities/basketHelpers';
import Engraving from './Engraving';

import styles from './Products.module.css';

interface ProductProps {
  product: SFCCProductItem;
  productGroup: ProductWithAttributes | undefined;
  eCommerceItem: EcommerceItem[];
  services?: SFCCProductItem[];
}

const BasketProductItem = ({
  product,
  productGroup,
  eCommerceItem,
  services = [],
}: ProductProps): React.JSX.Element => {
  const { setQuantity, removeProduct, patchProduct } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);
  const { sfcc } = useSiteSettings();
  const { t } = useFallbackTranslation();
  const handleDecreaseProductNumber = () => {
    if (!product.item_id) return;
    setQuantity(product.item_id, product.product_id, product.quantity - 1);
    gtmEvent(eCommercePayloadQuantityChange);
  };
  const handleIncreaseProductNumber = () => {
    if (!product.item_id) return;
    setQuantity(product.item_id, product.product_id, product.quantity + 1);
    const payload = {
      ...eCommercePayloadQuantityChange,
      event: GtmEventType.AddToCart,
    };
    gtmEvent(payload);
  };
  const eCommercePayload = useMemo(() => {
    const value = (eCommerceItem?.[0]?.price ?? 0) * (eCommerceItem?.[0]?.quantity ?? 0);

    return {
      event: GtmEventType.RemoveFromCart,
      ecommerce: {
        currency: sfcc?.currency || '',
        ...(value !== null && { value }),
        items: eCommerceItem,
      },
    };
  }, [sfcc, eCommerceItem]);
  const eCommercePayloadQuantityChange = useMemo(() => {
    const discountedPrice =
      product.price_after_order_discount !== undefined &&
      product.quantity !== null &&
      product.quantity > 0
        ? Math.round((product.price_after_order_discount / product.quantity) * 100) / 100
        : undefined;
    const adjustmentPrice = product.price_adjustments?.[0]?.price;
    const unitDiscount =
      adjustmentPrice !== null &&
      adjustmentPrice !== undefined &&
      product.quantity !== null &&
      product.quantity > 0
        ? Math.round((Math.abs(adjustmentPrice) / product.quantity) * 100) / 100
        : 0;

    return {
      ...eCommercePayload,
      ecommerce: {
        ...eCommercePayload.ecommerce!,
        ...(discountedPrice !== undefined && { value: discountedPrice }),

        items: eCommerceItem.map((item) => ({
          ...item,
          discount: unitDiscount,
          quantity: 1,
          ...(discountedPrice !== undefined && { price: discountedPrice }),
        })),
      },
    };
  }, [product, eCommercePayload, eCommerceItem]);

  const handleRemove = () => {
    if (!product.item_id) return;
    gtmEvent(eCommercePayload);
    removeProduct(product.item_id);
  };

  const existingGiftWrappingService = services?.find(
    (service) => service.c_serviceType === ServiceType.GiftWrapping
  );

  const hasGiftWrapping = !!existingGiftWrappingService;

  const handleRemoveGiftWrapping = () => {
    if (existingGiftWrappingService?.item_id) {
      removeProduct(existingGiftWrappingService.item_id);
    }
  };
  const handleAddGiftWrapping = (giftWrappingService: ProductService) => {
    if (!product.item_id) return;
    const newServices = [...services.map(mapToBasketService), giftWrappingService];
    patchProduct(product.item_id, product.quantity, newServices);
  };

  return (
    <div className={styles.border}>
      <div className={styles.product}>
        <img src={product.c_thumbnail_image_url} alt={product.product_name} />
        <div className={styles.right}>
          <div className={styles.text}>
            <p className={styles.name}>{product.item_text}</p>
            <p className={styles.id}>{product.product_id}</p>
            <p className={styles.price}>
              {product.c_price_formatted !== product.c_price_after_item_discount_formatted ? (
                <span className={styles.beforeDiscount}>{product.c_price_formatted}</span>
              ) : null}
              <span>{product.c_price_after_item_discount_formatted}</span>
            </p>
          </div>
          <div className={styles.quantityControls}>
            <div className="flex flex-1 flex-row items-center">
              <Button
                onClick={handleDecreaseProductNumber}
                variant="quaternary"
                size="s"
                aria-label={t('cart.DecreaseQuantity')}
                icon={<JabraIcon Icon={icon_minus} className={styles.icon} />}
              />

              <span className={styles.quantity}>{product.quantity}</span>
              <Button
                variant="quaternary"
                onClick={handleIncreaseProductNumber}
                size="s"
                aria-label={t('cart.IncreaseQuantity')}
                icon={<JabraIcon Icon={icon_plus} className={styles.icon} />}
              />
            </div>
            <button onClick={handleRemove} className="cursor-pointer text-[0.875rem] text-gray-800">
              <span>{t('cart.Remove')}</span>
            </button>
          </div>
        </div>
      </div>
      <Engraving product={product} />
      <FeatureToggle featureName={SiteFeature.SfccBasketGiftWrapping}>
        <GiftWrapping
          groupId={productGroup?.groupId}
          hasGiftWrapping={hasGiftWrapping}
          onAddGiftWrapping={handleAddGiftWrapping}
          onRemoveGiftWrapping={handleRemoveGiftWrapping}
        />
      </FeatureToggle>
    </div>
  );
};

const BasketProducts = (): ReactElement | null => {
  const { basket, busy, isBasketOpen, groups } = useBasket();
  const { gtmEvent } = useContext(TrackingContext);
  const { sfcc } = useSiteSettings();
  const basketItems = useMemo(() => basket?.product_items ?? [], [basket]);
  const products = basketItems.filter((p) => !p.c_isService);

  const eCommercePayload = useMemo(
    () =>
      createEcommercePayload({
        eventType: GtmEventType.ViewCart,
        basket,
        groups,
        currency: sfcc?.currency,
      }),
    [basket, groups, sfcc?.currency]
  );

  const isGtmBasketViewEventSent = useRef(false);
  useEffect(() => {
    const isECommercePayloadUpdated = !busy;
    if (isBasketOpen && isECommercePayloadUpdated && !isGtmBasketViewEventSent.current) {
      gtmEvent(eCommercePayload);
      isGtmBasketViewEventSent.current = true;
    } else if (!isBasketOpen) {
      isGtmBasketViewEventSent.current = false;
    }
  }, [isBasketOpen, busy, eCommercePayload, gtmEvent]);

  return (
    <div className={classNames(styles.container)}>
      {products.map((product) => {
        const services = product.c_serviceIDs
          ?.map((serviceId) => basketItems.find((itm) => itm.c_productLineItemID === serviceId))
          .filter(isNotNil);

        const productGroup = groups.find((p) => p.sku === product.product_id);
        return (
          <BasketProductItem
            key={product.item_id}
            product={product}
            services={services}
            productGroup={productGroup}
            eCommerceItem={eCommercePayload.ecommerce!.items.filter(
              (item) => item.item_id === product.product_id
            )}
          />
        );
      })}
    </div>
  );
};

export default BasketProducts;
